import { useState, useRef, useEffect } from 'react';
import { 
  Zap, 
  Bell, 
  Lock, 
  Globe, 
  MapPin, 
  ChevronDown, 
  Check, 
  WifiOff, 
  Clock, 
  Building2,
  ExternalLink,
  Download
} from 'lucide-react';
import { ContractedPlace, ContractStatus, UserAccount } from '../types/energy';
import { contractStatusLabel, placeOrdinal } from '../services/portalPlaces';
import { Language, translations } from '../i18n/translations';
import { usePWAInstall } from '../hooks/usePWAInstall';

interface TopNavProps {
  account: UserAccount;
  places: ContractedPlace[];
  selectedPlaceId: string;
  onSelectPlace: (placeId: string) => void;
  unreadCount: number;
  onOpenNotifications: () => void;
  onLockApp: () => void;
  lang: Language;
  onToggleLanguage: () => void;
  isOffline?: boolean;
  nextSyncDisplay?: string;
  onOpenInstallModal?: () => void;
}

export function TopNav({
  account,
  places = [],
  selectedPlaceId,
  onSelectPlace,
  unreadCount,
  onOpenNotifications,
  onLockApp,
  lang,
  onToggleLanguage,
  isOffline = false,
  nextSyncDisplay,
  onOpenInstallModal
}: TopNavProps) {
  const { isInstalled } = usePWAInstall();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = translations[lang];

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentPlace = places.find((p) => p.id === selectedPlaceId) || places[0];

  const getStatusLabel = (status?: ContractStatus): string => {
    if (lang === 'ja') return status || '';
    return contractStatusLabel(status, t);
  };

  const getPlanLabel = (plan: string) => {
    if (lang === 'ja') return plan;
    return plan.includes('夜') ? '【Night】Life Fit Plan' : plan;
  };

  // The portal's place label starts with an ordinal (２＿アハマド シャヒル), so "Location 2" is
  // read off that label; roster order is the fallback and demandNumber keeps the EN UI kanji-free.
  const getPlaceDisplayName = (place?: ContractedPlace, rosterIndex?: number) => {
    if (!place) return '';
    if (lang === 'ja') return place.displayName || place.demandNumber || '';
    const ordinal = placeOrdinal(place, rosterIndex);
    if (ordinal) return `${t.locationLabel} ${ordinal}`;
    return place.demandNumber || '';
  };

  return (
    <header className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/80 px-3 sm:px-4 py-2.5">
      <div className="max-w-4xl mx-auto flex flex-col gap-2">
        {/* Main Navbar Row */}
        <div className="flex items-center justify-between gap-2">
          {/* Brand & Contracted Place Zone */}
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Logo */}
            <div className="w-8 h-8 rounded-xl bg-teal-500/20 border border-teal-500/40 text-teal-400 flex items-center justify-center shrink-0 shadow-sm shadow-teal-500/10">
              <Zap className="w-4 h-4 fill-teal-400" />
            </div>

            <div className="min-w-0 flex flex-col">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-extrabold tracking-tight text-white">
                  {t.brandName}
                </span>
                <span className="text-[10px] font-mono text-teal-400 font-normal px-1 py-0.2 rounded bg-teal-950/80 border border-teal-800/40">
                  {t.nativeBadge}
                </span>
                {isOffline && (
                  <span className="flex items-center gap-1 text-[10px] font-medium text-amber-400 bg-amber-950/60 border border-amber-800/60 rounded px-1.5 py-0.5">
                    <WifiOff className="w-2.5 h-2.5" />
                    <span>{lang === 'ja' ? 'オフライン' : 'Offline'}</span>
                  </span>
                )}
              </div>

              {/* Customer ID / Name */}
              <p className="text-[10px] text-slate-400 truncate max-w-[140px] sm:max-w-[220px]">
                {account.accountHolder || (lang === 'ja' ? '未連携' : 'Not Connected')}
                {account.accountNumber ? (
                  <span className="text-slate-500 font-mono ml-1">({account.accountNumber})</span>
                ) : null}
              </p>
            </div>
          </div>

          {/* Right Action Icons */}
          <div className="flex items-center gap-1.5 shrink-0">
            {/* 1:00 AM Auto-Sync Indicator Pill (Desktop/Tablet) */}
            <div 
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-slate-900 border border-slate-800 text-[11px] text-slate-300"
              title={lang === 'ja' ? '毎日午前1:00に自動同期を実行します (ローカル保存済み)' : 'Auto-syncs daily at 01:00 AM (Saved locally)'}
            >
              <Clock className="w-3.5 h-3.5 text-teal-400" />
              <span className="text-slate-400 font-mono">01:00</span>
              <span className="text-[10px] text-teal-400 font-medium">Auto-Sync</span>
            </div>

            {/* PWA Install Button (hidden when running in standalone mode) */}
            {!isInstalled && onOpenInstallModal && (
              <button
                type="button"
                onClick={onOpenInstallModal}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-gradient-to-r from-teal-500/20 to-emerald-500/20 hover:from-teal-500/30 hover:to-emerald-500/30 text-teal-300 hover:text-teal-200 border border-teal-500/40 text-xs font-semibold transition-all shadow-sm active:scale-95 cursor-pointer"
                title={lang === 'ja' ? 'ホーム画面にアプリをインストール' : 'Install App to Home Screen'}
              >
                <Download className="w-3.5 h-3.5" />
                <span className="hidden xs:inline">{lang === 'ja' ? 'インストール' : 'Install'}</span>
              </button>
            )}

            {/* Language Switcher Segmented Control (JA / EN) */}
            <div className="flex items-center p-0.5 rounded-xl bg-slate-900 border border-slate-800 text-xs shadow-sm">
              <button
                type="button"
                onClick={() => { if (lang !== 'ja') onToggleLanguage(); }}
                className={`px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                  lang === 'ja'
                    ? 'bg-teal-500 text-slate-950 shadow'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                title="日本語"
              >
                日
              </button>
              <button
                type="button"
                onClick={() => { if (lang !== 'en') onToggleLanguage(); }}
                className={`px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                  lang === 'en'
                    ? 'bg-teal-500 text-slate-950 shadow'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                title="English"
              >
                EN
              </button>
            </div>

            {/* Notifications bell */}
            <button
              type="button"
              onClick={onOpenNotifications}
              className="relative min-w-[38px] min-h-[38px] flex items-center justify-center text-slate-300 hover:text-white rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-800 transition-colors"
              title={t.notifications}
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-teal-400 ring-2 ring-slate-950" />
              )}
            </button>

            {/* Quick lock app */}
            <button
              type="button"
              onClick={onLockApp}
              className="min-w-[38px] min-h-[38px] flex items-center justify-center text-slate-400 hover:text-slate-200 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-800 transition-colors"
              title={t.lockApp}
            >
              <Lock className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Contracted Places Selector Bar on Navbar */}
        {places && places.length > 0 && (
          <div className="relative pt-1 border-t border-slate-800/60" ref={dropdownRef}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold text-slate-400 flex items-center gap-1 shrink-0">
                <MapPin className="w-3 h-3 text-teal-400" />
                <span>{lang === 'ja' ? 'ご使用場所:' : 'Location:'}</span>
              </span>

              {/* Location Select Button */}
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex-1 flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl bg-slate-900/90 hover:bg-slate-800/90 border border-slate-800 hover:border-slate-700 transition-all text-left text-xs"
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <div className="flex items-center gap-2 truncate">
                  <span className="font-semibold text-slate-100 truncate">
                    {getPlaceDisplayName(currentPlace, places.indexOf(currentPlace)) || currentPlace?.demandNumber}
                  </span>
                  <span className="font-mono text-[10px] text-slate-400 hidden sm:inline">
                    ({currentPlace?.demandNumber})
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${
                    currentPlace?.status === '契約中'
                      ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                      : 'bg-slate-800 text-slate-400 border border-slate-700'
                  }`}>
                    {getStatusLabel(currentPlace?.status)}
                  </span>
                </div>

                <div className="flex items-center gap-1 text-slate-400 shrink-0">
                  <span className="text-[10px] hidden sm:inline text-slate-400 font-mono">
                    {places.length} {lang === 'ja' ? '箇所' : 'places'}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-teal-400' : ''}`} />
                </div>
              </button>
            </div>

            {/* Dropdown Menu Modal */}
            {dropdownOpen && (
              <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-fadeIn backdrop-blur-xl">
                <div className="px-3.5 py-2.5 bg-slate-950/80 border-b border-slate-800/80 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-200 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-teal-400" />
                    <span>{lang === 'ja' ? 'ご使用場所一覧の選択' : 'Select Contract Location'}</span>
                  </span>
                  <span className="text-[10px] text-teal-400 font-medium">
                    {places.length} {lang === 'ja' ? '契約登録済' : 'Registered'}
                  </span>
                </div>

                <div className="max-h-64 overflow-y-auto divide-y divide-slate-800/60 p-1.5">
                  {places.map((place) => {
                    const isSelected = place.id === selectedPlaceId;
                    const isActive = place.status === '契約中';

                    return (
                      <button
                        key={place.id}
                        type="button"
                        onClick={() => {
                          onSelectPlace(place.id);
                          setDropdownOpen(false);
                        }}
                        className={`w-full p-2.5 rounded-xl text-left transition-colors flex items-start justify-between gap-3 ${
                          isSelected
                            ? 'bg-teal-500/10 border border-teal-500/30 text-white'
                            : 'hover:bg-slate-800/60 text-slate-300'
                        }`}
                      >
                        <div className="space-y-1 min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-white truncate">
                              {getPlaceDisplayName(place)}
                            </span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                              isActive
                                ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                                : 'bg-slate-800 text-slate-400 border border-slate-700'
                            }`}>
                              {getStatusLabel(place.status)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-[11px] text-slate-400">
                            <span className="font-mono text-teal-400">{place.demandNumber}</span>
                            <span>·</span>
                            <span>{getPlanLabel(place.planName)}</span>
                            <span>·</span>
                            <span>{place.contractCapacity}</span>
                          </div>

                          <p className="text-[10px] text-slate-400 truncate">
                            {place.address && !place.address.includes('千代田区1-1') 
                              ? place.address 
                              : (lang === 'ja' ? 'ご使用場所住所: マイページ連携で取得' : 'Location: Not Set')}
                          </p>
                        </div>

                        <div className="shrink-0 pt-1">
                          {isSelected ? (
                            <div className="w-5 h-5 rounded-full bg-teal-500 text-slate-950 flex items-center justify-center">
                              <Check className="w-3.5 h-3.5 stroke-[3]" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full border border-slate-700 hover:border-slate-500" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {nextSyncDisplay && (
                  <div className="px-3 py-2 bg-slate-950/60 border-t border-slate-800/80 text-[10px] text-slate-400 flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-teal-400" />
                      <span>{lang === 'ja' ? '次回定時自動同期:' : 'Next Auto-Sync:'}</span>
                    </span>
                    <span className="font-mono text-teal-300">{nextSyncDisplay}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
